import type { ISODateString, Loan, LoanEvent } from '../../types';
import { computeSchedule, countGenuineCycles } from './engine';
import { calculateEmi, calculateTenureMonthsRaw } from './emi';

/**
 * ============================================================================
 * BASIS CALCULATION — for repayment-consequence suggestions only
 * ============================================================================
 *
 * This is NOT the final schedule calculation. It answers a narrower
 * question used only by the consequence sub-forms (rate change,
 * prepayment, balance correction): "what is the loan's PROJECTED state
 * immediately before THIS event, so I can suggest an EMI or tenure?"
 *
 * APPROVED RULE (chronological exclusion, applies identically to both
 * creating a new event and editing an existing one — no separate
 * "identity exclusion" logic):
 *
 *   basis = all active events with effectiveDate STRICTLY EARLIER than
 *   the event currently being created/edited.
 *
 *   - The event itself is never included in its own basis.
 *   - No event with an effectiveDate on/after the event being
 *     created/edited is included, regardless of whether it existed
 *     before this edit — editing an earlier event must never let a
 *     later event's stored consequence leak backward into the basis,
 *     since that later event's own numbers were themselves calculated
 *     assuming the (about-to-be-edited) earlier event's ORIGINAL values.
 *
 * CRITICAL PRINCIPLE (corrected after review):
 * `loan.originalTenureMonths` (e.g. 300) is the STARTING tenure only —
 * it is NEVER treated as a current or remaining value anywhere in this
 * file. The current PROJECTED remaining tenure is derived by asking the
 * calculation engine to project the loan's CURRENT trajectory (as
 * established by every prior event — prior prepayments, prior rate
 * changes, prior repayment-consequence changes, all already reflected in
 * `basisEvents` and thus already "baked into" whatever EMI/rate the
 * engine resolves for any given day) forward to its own natural closure,
 * and counting cycles from there. This is NEVER computed as
 * `originalTenureMonths - elapsedCycles`, and NEVER by reverse-solving
 * the EMI formula against the original tenure — both would silently
 * ignore any prior event that already changed the trajectory.
 *
 * REFINEMENT — residual closing payments are excluded from tenure counts:
 * the engine's daily Actual/365 accrual does not always exactly
 * reproduce what the standard monthly-compounding EMI formula assumed,
 * so a trajectory can require one small, mechanically-generated final
 * payment beyond the regular EMI amount to fully clear the balance (see
 * ScheduleMonth.isResidualClosingPayment). That payment is real and
 * remains accurate in the schedule itself, but it is NOT a scheduled/
 * contractual tenure cycle — both `cyclesCompleted` and
 * `totalCyclesUnderTrajectory` below count only cycles where
 * isResidualClosingPayment is false, so remainingTenureMonths reflects
 * genuine repayment cycles only.
 *
 * PRE-EMI NOTE: interest-only pre-EMI rows are not tenure cycles
 * (countGenuineCycles counts REGULAR cycles only). If regular repayment
 * has not been recorded as commenced, the trajectory contains no regular
 * cycles, so remainingTenureMonths is 0 and no tenure/EMI suggestion can
 * be derived from it — the consequence form then leaves the derived
 * value blank for the user to enter explicitly, never inventing one.
 *
 * IMPORTANT: this basis calculation has NO lasting effect on stored
 * data. It only powers a suggestion shown in a form. Once the user
 * saves, only the final newEmi/newRemainingTenureMonths/primaryLever
 * values are persisted on the event — the FINAL schedule (via
 * computeSchedule() over the complete, unfiltered active event list) is
 * always what Schedule/Dashboard/Analytics/Report actually display, and
 * it is always recalculated from the beginning after any edit.
 */

export interface RepaymentBasis {
  /** Outstanding principal immediately before the event being created/edited. */
  outstandingPrincipal: number;
  /** The interest rate that would be applicable immediately before the event being created/edited (or the live-entered new rate, when liveRateOverride is supplied — see computeRepaymentBasis). */
  applicableAnnualRatePercent: number;
  /**
   * The EMI actually in effect immediately before the event being
   * created/edited — i.e. whatever EMI the current trajectory has
   * carried forward from the most recent prior consequence (or the
   * loan's original EMI, if no consequence has applied yet). This is
   * used by the "Keep current EMI" checkbox behaviour in the repayment-
   * consequence UI: when checked, THIS value (never liveRateOverride-
   * affected, never the tenure-derived suggestion) is held fixed and
   * tenure is recalculated from it at the new rate/principal instead.
   */
  currentActiveEmi: number;
  /**
   * The number of EMI cycles remaining under the CURRENT PROJECTED
   * TRAJECTORY, immediately before the event being created/edited — NOT
   * `loan.originalTenureMonths` minus elapsed cycles, and NOT solved by
   * inverting the EMI formula. Derived by projecting basisEvents forward
   * (with no further events / no asOfDate cap) to the trajectory's own
   * natural closure and subtracting cycles already completed by the
   * target date. See computeRepaymentBasis for the exact method.
   *
   * CRITICAL: this value is ALWAYS computed from history alone (the
   * trajectory as it stood immediately BEFORE the event currently being
   * created/edited) and is NEVER affected by `liveRateOverride` or any
   * other in-progress, not-yet-saved data about the event itself. A rate
   * change (or prepayment) does not retroactively alter how many cycles
   * were already scheduled to remain — it only changes what EMI is
   * needed to clear that SAME remaining tenure at the new rate/principal.
   * This is what makes "remaining tenure is the default primary lever,
   * EMI recalculates live" the correct default behaviour: the tenure
   * side of the basis is fixed by history the moment the target date is
   * chosen, and only applicableAnnualRatePercent (and, via
   * principalAdjustment, outstandingPrincipal) responds to what the user
   * is currently typing.
   */
  remainingTenureMonths: number;
}

/**
 * Computes the basis for a repayment-consequence suggestion.
 *
 * @param loan The loan.
 * @param allActiveEvents Every currently-active event for the loan (as
 *   returned by eventsRepo.listEvents) — this function does the
 *   chronological filtering itself; callers should NOT pre-filter.
 * @param targetEffectiveDate The effectiveDate of the event currently
 *   being created or edited. Events with this exact effectiveDate are
 *   EXCLUDED (per the strictly-earlier rule) — including, when editing,
 *   the event's own prior version, since that version is filtered out
 *   by the same date-based rule as any other same-or-later event
 *   without needing to special-case "is this the event being edited".
 * @param liveRateOverride Optional — for a RATE CHANGE event specifically,
 *   the NEW rate currently being typed into the form, not yet saved as
 *   an event. Affects ONLY `applicableAnnualRatePercent` in the returned
 *   basis (so the EMI derived FROM the unchanged remaining tenure
 *   reflects the newly-typed rate live) — it deliberately does NOT
 *   affect `remainingTenureMonths`, which always reflects the trajectory
 *   as it stood before this event, independent of what rate is currently
 *   being entered for the event itself. Ignored for prepayment/balance-
 *   correction consequences, which don't change the rate.
 */
export function computeRepaymentBasis(
  loan: Loan,
  allActiveEvents: LoanEvent[],
  targetEffectiveDate: ISODateString,
  liveRateOverride?: number
): RepaymentBasis {
  const basisEvents = allActiveEvents.filter((e) => e.effectiveDate < targetEffectiveDate);

  // --- Principal and rate immediately before the target event ---
  // (asOfDate caps the walk exactly at the target date, so this reflects
  // the state right up to, but not including, the event being entered.)
  const beforeEventSchedule = computeSchedule({ loan, events: basisEvents, asOfDate: targetEffectiveDate });
  const outstandingPrincipal = beforeEventSchedule.summary.currentOutstandingPrincipal;
  const applicableAnnualRatePercent = liveRateOverride ?? resolveRateAsOf(loan, basisEvents, targetEffectiveDate);
  // Never affected by liveRateOverride — this is the EMI actually in
  // effect right now, before whatever new rate is being typed for THIS
  // event, regardless of what that new rate is.
  const currentActiveEmi = beforeEventSchedule.summary.currentApplicableEmi;

  // --- Remaining tenure under the CURRENT PROJECTED TRAJECTORY ---
  // Step 1: GENUINE (non-residual) cycles already completed by
  // targetEffectiveDate. A mechanically-generated residual closing
  // payment (see ScheduleMonth.isResidualClosingPayment) is never a
  // scheduled/contractual tenure cycle, so it is excluded from this
  // count — counting it would inflate "cycles completed" by a payment
  // that isn't a regular installment.
  const cyclesCompleted = countGenuineCycles(beforeEventSchedule);

  // Step 2: project the CURRENT trajectory (basisEvents ONLY — never
  // including liveRateOverride, and never including the event being
  // created/edited) forward with NO asOfDate cap, so the engine walks
  // all the way to THIS (pre-event) trajectory's own natural closure.
  // Every prior prepayment, rate change, and repayment-consequence
  // change is already reflected in basisEvents and therefore already
  // shapes this projection automatically via the engine's existing
  // per-day rate/EMI resolution — no special-casing needed here.
  //
  // liveRateOverride is DELIBERATELY EXCLUDED from this projection: the
  // remaining-tenure figure must represent history alone, not a
  // hypothetical "what if the new rate had always applied" trajectory —
  // mixing an old-rate "before" segment with a new-rate "after" segment
  // in a single total-cycle-count would not correctly subtract against
  // cyclesCompleted (which is always measured at the OLD, historical
  // rate), producing an inflated and incorrect remaining-tenure figure.
  // See repaymentConsequenceState.ts / RepaymentConsequenceFields.tsx
  // for how the NEW rate is instead applied: only to derive EMI from
  // this UNCHANGED remaining tenure, via deriveOtherLever.
  const totalCyclesUnderTrajectory = countGenuineCycles(computeSchedule({ loan, events: basisEvents }));

  const remainingTenureMonths = Math.max(0, totalCyclesUnderTrajectory - cyclesCompleted);

  return {
    outstandingPrincipal,
    applicableAnnualRatePercent,
    currentActiveEmi,
    remainingTenureMonths,
  };
}

/**
 * Small local helper mirroring the engine's own rate-resolution logic
 * (src/lib/calc/engine.ts's resolveRateForDay), but exposed here since
 * that function is internal to the engine module. Duplicated logic is
 * intentionally tiny (a handful of lines) and kept in sync by both
 * living in this same package; this avoids exporting an engine-internal
 * function purely for one external caller.
 */
function resolveRateAsOf(loan: Loan, events: LoanEvent[], asOfDate: ISODateString): number {
  let rate = loan.originalAnnualRatePercent;
  const rateChanges = events
    .filter((e): e is Extract<LoanEvent, { type: 'rate_change' }> => e.type === 'rate_change')
    .sort((a, b) => (a.effectiveDate < b.effectiveDate ? -1 : a.effectiveDate > b.effectiveDate ? 1 : 0));
  for (const rc of rateChanges) {
    if (rc.effectiveDate > asOfDate) break;
    rate = rc.annualRatePercent;
  }
  return rate;
}

/**
 * Given a RepaymentBasis and a chosen primary lever + its value, computes
 * the suggested value for the OTHER lever. Used directly by the
 * repayment-consequence UI components — this is the single place that
 * logic lives, so the UI never duplicates the EMI<->tenure formulas
 * itself.
 *
 * Returns undefined for the derived value if it cannot be calculated
 * (e.g. an EMI too low to cover interest) — callers must handle this by
 * leaving the field blank/showing a message, never by defaulting to 0.
 */
export function deriveOtherLever(
  basis: Pick<RepaymentBasis, 'outstandingPrincipal' | 'applicableAnnualRatePercent'>,
  primaryLever: 'emi' | 'tenure',
  primaryValue: number
): number | undefined {
  if (primaryLever === 'emi') {
    const tenureRaw = calculateTenureMonthsRaw(basis.outstandingPrincipal, basis.applicableAnnualRatePercent, primaryValue);
    return tenureRaw === undefined ? undefined : Math.ceil(tenureRaw);
  }
  return calculateEmi(basis.outstandingPrincipal, basis.applicableAnnualRatePercent, primaryValue);
}
